import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CATEGORIES } from '../categories';

const mono = { fontFamily: "'JetBrains Mono', monospace" };
const sans = { fontFamily: "var(--font-display)" };

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const min  = Math.floor(diff / 60000);
  const hrs  = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (min  < 1)  return 'Gerade eben';
  if (min  < 60) return `Vor ${min} Min.`;
  if (hrs  < 24) return `Vor ${hrs} Std.`;
  if (days < 30) return `Vor ${days} Tag${days > 1 ? 'en' : ''}`;
  return new Date(dateStr).toLocaleDateString('de-DE');
}

const TYPE_ICON = {
  join_request:    '📩',
  invite_accepted: '👤',
  event_reminder:  '🔔',
  team_invite:     '📬',
  approved:        '✅',
  driver_left:     '🚪',
  driver_removed:  '⛔',
};

const RECIPIENT_MODE_LABELS = {
  all:      'ALLE',
  category: 'RENNKLASSE',
  selected: 'EINZELNE FAHRER',
};

function Notifications({ token, role, isCoAdmin = false, isRacing = false }) {
  const isAdmin          = role === 'admin';
  const isAdminOrCoAdmin = isAdmin || isCoAdmin;
  // fahrer tab: co-admins always have a driver account; owner only when isRacing
  const showFahrerTab    = isCoAdmin || (isAdmin && isRacing);
  const navigate         = useNavigate();

  const [activeTab, setActiveTab] = useState(isAdminOrCoAdmin ? 'team' : 'fahrer');

  // Sync tab when role/racing changes live
  useEffect(() => {
    if ((activeTab === 'team' || activeTab === 'senden') && !isAdminOrCoAdmin) setActiveTab('fahrer');
    if (activeTab === 'fahrer' && isAdminOrCoAdmin && !showFahrerTab) setActiveTab('team');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFahrerTab, isAdminOrCoAdmin]);

  // ── Notification lists ────────────────────────────────────────────────────
  const [teamNotifs,    setTeamNotifs]    = useState([]);
  const [teamLoading,   setTeamLoading]   = useState(true);
  const [driverNotifs,  setDriverNotifs]  = useState([]);
  const [driverLoading, setDriverLoading] = useState(false);
  const [markingAll,    setMarkingAll]    = useState(false);
  const [regMap,        setRegMap]        = useState({});
  const [regPending,    setRegPending]    = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const isDriverTab   = !isAdminOrCoAdmin || activeTab === 'fahrer';
  const notifications = isDriverTab ? driverNotifs : teamNotifs;
  const loading       = isDriverTab ? driverLoading : teamLoading;

  const readAllUrl = isDriverTab ? '/api/notifications/driver/read-all' : '/api/notifications/read-all';
  const readOneUrl = (id) => isDriverTab
    ? `/api/notifications/driver/${id}/read`
    : `/api/notifications/${id}/read`;

  const loadTeam = useCallback(async () => {
    if (!isAdminOrCoAdmin) return;
    setTeamLoading(true);
    try {
      const res = await axios.get('/api/notifications', { headers });
      setTeamNotifs(res.data);
    } catch (err) { console.error(err); }
    finally { setTeamLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadDriver = useCallback(async () => {
    setDriverLoading(true);
    try {
      const res = await axios.get('/api/notifications/driver', { headers });
      setDriverNotifs(res.data);
    } catch (err) { console.error(err); }
    finally { setDriverLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadRegs = useCallback(async () => {
    try {
      const res = await axios.get('/api/registrations', { headers });
      const map = {};
      for (const r of res.data) map[r.event_id] = r;
      setRegMap(map);
    } catch (_) {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const load = useCallback(async () => {
    if (isAdminOrCoAdmin) loadTeam();
    if (!isAdmin || isRacing) { loadDriver(); loadRegs(); }
  }, [isAdminOrCoAdmin, isAdmin, isRacing, loadTeam, loadDriver, loadRegs]);

  useEffect(() => {
    if (isAdminOrCoAdmin) loadTeam();
    if (!isAdmin || isRacing) { loadDriver(); loadRegs(); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCoAdmin, isRacing]);

  useEffect(() => { load(); }, [load]);

  const setNotifications = (updater) => {
    if (isDriverTab) setDriverNotifs(updater);
    else setTeamNotifs(updater);
  };

  const markRead = async (id) => {
    try {
      await axios.patch(readOneUrl(id), {}, { headers });
      setNotifications(ns => ns.map(n => n.id === id ? { ...n, read: true } : n));
    } catch (err) { console.error(err); }
  };

  const markAllRead = async () => {
    setMarkingAll(true);
    try {
      await axios.patch(readAllUrl, {}, { headers });
      setNotifications(ns => ns.map(n => ({ ...n, read: true })));
    } catch (err) { console.error(err); }
    finally { setMarkingAll(false); }
  };

  const handleEventToggle = async (n) => {
    const eventId = n.event_id;
    if (!eventId) return;
    setRegPending(eventId);
    try {
      const existing = regMap[eventId];
      const isIn = existing?.status === 'participating' || existing?.status === 'assigned';
      if (isIn) {
        await axios.delete(`/api/registrations/${existing.id}`, { headers });
        setRegMap(m => { const next = { ...m }; delete next[eventId]; return next; });
      } else {
        const statusRes = await axios.get('/api/auth/driver-status', { headers });
        const driverId = statusRes.data.driver_id;
        if (!driverId) return;
        const res = await axios.post('/api/registrations', {
          event_id: eventId, driver_id: driverId, status: 'participating',
        }, { headers });
        setRegMap(m => ({ ...m, [eventId]: { id: res.data.id, event_id: eventId, driver_id: driverId, status: 'participating' } }));
      }
      if (!n.read) markRead(n.id);
    } catch (err) { console.error(err); }
    finally { setRegPending(null); }
  };

  const unreadCount  = notifications.filter(n => !n.read).length;
  const teamUnread   = teamNotifs.filter(n => !n.read).length;
  const driverUnread = driverNotifs.filter(n => !n.read).length;
  const teamTabLabel = isAdmin ? 'OWNER' : 'TEAM';

  // ── SENDEN tab state ─────────────────────────────────────────────────────
  const [sendTitle,         setSendTitle]         = useState('');
  const [sendBody,          setSendBody]           = useState('');
  const [recipientMode,     setRecipientMode]      = useState('all');
  const [selectedCats,      setSelectedCats]       = useState([]);
  const [drivers,           setDrivers]            = useState([]);
  const [selectedDriverIds, setSelectedDriverIds]  = useState([]);
  const [events,            setEvents]             = useState([]);
  const [selectedEventId,   setSelectedEventId]    = useState('');
  const [sending,           setSending]            = useState(false);
  const [sendResult,        setSendResult]         = useState(null);
  const [history,           setHistory]            = useState([]);
  const [historyLoading,    setHistoryLoading]     = useState(false);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get('/api/push/custom/history', { headers });
      setHistory(res.data || []);
    } catch (err) { console.error(err); }
    finally { setHistoryLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadSendenData = useCallback(async () => {
    try {
      const [dRes, eRes] = await Promise.all([
        axios.get('/api/drivers', { headers }),
        axios.get('/api/events', { headers }),
      ]);
      setDrivers(dRes.data || []);
      setEvents(eRes.data || []);
    } catch (err) { console.error(err); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (activeTab === 'senden' && isAdminOrCoAdmin) {
      loadSendenData();
      loadHistory();
    }
    // Clear stale send result when switching tabs
    setSendResult(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, loadSendenData, loadHistory]);

  const toggleCat = (cat) => {
    setSelectedCats(prev =>
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  };

  const toggleDriver = (id) => {
    setSelectedDriverIds(prev =>
      prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]
    );
  };

  const handleSend = async () => {
    if (!sendTitle.trim() || !sendBody.trim()) return;
    if (recipientMode === 'category' && selectedCats.length === 0) return;
    if (recipientMode === 'selected' && selectedDriverIds.length === 0) return;
    setSending(true);
    setSendResult(null);
    try {
      let recipientMeta = null;
      if (recipientMode === 'category') recipientMeta = { categories: selectedCats };
      if (recipientMode === 'selected') recipientMeta = { driverAccountIds: selectedDriverIds };
      const res = await axios.post('/api/push/custom', {
        title: sendTitle.trim(),
        body: sendBody.trim(),
        eventId: selectedEventId ? Number(selectedEventId) : null,
        recipientMode,
        recipientMeta,
      }, { headers });
      setSendResult(res.data);
      setSendTitle(''); setSendBody('');
      setRecipientMode('all'); setSelectedCats([]); setSelectedDriverIds([]);
      setSelectedEventId('');
      loadHistory();
    } catch (err) {
      setSendResult({ error: err.response?.data?.error || 'Fehler beim Senden.' });
    } finally {
      setSending(false);
    }
  };

  // ── Build tabs ────────────────────────────────────────────────────────────
  const showTabs = isAdminOrCoAdmin; // always tabs for admin/co-admin
  const tabDefs = isAdminOrCoAdmin
    ? [
        ['team',   teamTabLabel, teamUnread],
        ...(showFahrerTab ? [['fahrer', 'FAHRER', driverUnread]] : []),
        ['senden', 'SENDEN',    0],
      ]
    : [];

  // ── Input style helpers ───────────────────────────────────────────────────
  const inputStyle = {
    width: '100%', boxSizing: 'border-box',
    background: 'rgba(26,28,32,0.6)',
    border: '1px solid rgba(59,73,75,0.4)',
    color: 'var(--on-surface)',
    padding: '10px 14px',
    ...mono, fontSize: 12,
    outline: 'none',
    transition: 'border-color 0.15s',
  };
  const labelStyle = { ...mono, fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--outline)', display: 'block', marginBottom: 6 };
  const sectionStyle = { marginBottom: 20 };

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ ...mono, fontSize: 9, fontWeight: 700, letterSpacing: '0.2em', color: 'var(--primary)', marginBottom: 6, textTransform: 'uppercase' }}>
            INBOX
          </div>
          <h1 style={{ margin: 0 }}>NOTIFICATIONS</h1>
        </div>
        {/* Only show 'mark all read' when on an inbox tab */}
        {activeTab !== 'senden' && (
          <button
            onClick={markAllRead}
            disabled={markingAll || unreadCount === 0}
            style={{ ...mono, fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', padding: '8px 16px', background: 'transparent', border: `1px solid ${unreadCount > 0 ? 'rgba(var(--primary-rgb),0.35)' : 'rgba(59,73,75,0.25)'}`, color: unreadCount > 0 ? 'var(--primary)' : 'var(--outline)', cursor: unreadCount > 0 ? 'pointer' : 'not-allowed', opacity: markingAll ? 0.5 : 1, whiteSpace: 'nowrap', transition: 'all 0.15s' }}
            onMouseEnter={e => { if (unreadCount > 0) e.currentTarget.style.background = 'rgba(var(--primary-rgb),0.07)'; }}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            ALLE ALS GELESEN MARKIEREN
          </button>
        )}
      </div>

      {/* ── Tabs ── */}
      {showTabs ? (
        <div style={{ display: 'flex', borderBottom: '1px solid rgba(59,73,75,0.3)', marginBottom: '1.5rem' }}>
          {tabDefs.map(([key, label, badge]) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              style={{ padding: '8px 18px', background: 'transparent', border: 'none', borderBottom: activeTab === key ? '2px solid var(--primary)' : '2px solid transparent', color: activeTab === key ? 'var(--primary)' : '#849495', ...mono, fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', cursor: 'pointer', marginBottom: -1, transition: 'color 0.15s', display: 'flex', alignItems: 'center', gap: 7 }}
            >
              {label}
              {badge > 0 && (
                <span style={{ background: 'var(--primary)', color: '#002022', borderRadius: 8, padding: '1px 6px', fontSize: 8, fontWeight: 700 }}>{badge}</span>
              )}
            </button>
          ))}
        </div>
      ) : (
        <div style={{ marginBottom: '1.5rem' }} />
      )}

      {/* ── SENDEN Tab ── */}
      {activeTab === 'senden' && isAdminOrCoAdmin && (
        <div>
          {/* Form */}
          <div style={{ border: '1px solid rgba(59,73,75,0.3)', background: 'rgba(26,28,32,0.4)', padding: '1.5rem', marginBottom: '2rem' }}>
            <div style={{ ...mono, fontSize: 9, fontWeight: 700, letterSpacing: '0.18em', color: 'var(--primary)', marginBottom: 20 }}>
              NACHRICHT VERFASSEN
            </div>

            {/* Title */}
            <div style={sectionStyle}>
              <label style={labelStyle}>TITEL</label>
              <input
                type="text"
                placeholder="Betreff der Nachricht..."
                value={sendTitle}
                onChange={e => setSendTitle(e.target.value)}
                maxLength={120}
                style={inputStyle}
                onFocus={e => e.currentTarget.style.borderColor = 'rgba(var(--primary-rgb),0.5)'}
                onBlur={e => e.currentTarget.style.borderColor = 'rgba(59,73,75,0.4)'}
              />
            </div>

            {/* Body */}
            <div style={sectionStyle}>
              <label style={labelStyle}>NACHRICHT</label>
              <textarea
                placeholder="Text der Push Notification..."
                value={sendBody}
                onChange={e => setSendBody(e.target.value)}
                maxLength={500}
                rows={4}
                style={{ ...inputStyle, resize: 'vertical' }}
                onFocus={e => e.currentTarget.style.borderColor = 'rgba(var(--primary-rgb),0.5)'}
                onBlur={e => e.currentTarget.style.borderColor = 'rgba(59,73,75,0.4)'}
              />
              <div style={{ ...mono, fontSize: 8, color: 'var(--outline)', marginTop: 4, textAlign: 'right' }}>
                {sendBody.length}/500
              </div>
            </div>

            {/* Recipient mode */}
            <div style={sectionStyle}>
              <label style={labelStyle}>EMPFÄNGER</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {['all', 'category', 'selected'].map(mode => (
                  <button
                    key={mode}
                    onClick={() => { setRecipientMode(mode); setSelectedCats([]); setSelectedDriverIds([]); }}
                    style={{ ...mono, fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', padding: '7px 14px', background: recipientMode === mode ? 'rgba(var(--primary-rgb),0.15)' : 'transparent', border: `1px solid ${recipientMode === mode ? 'rgba(var(--primary-rgb),0.6)' : 'rgba(59,73,75,0.35)'}`, color: recipientMode === mode ? 'var(--primary)' : '#849495', cursor: 'pointer', transition: 'all 0.15s' }}
                  >
                    {RECIPIENT_MODE_LABELS[mode]}
                  </button>
                ))}
              </div>
            </div>

            {/* Category pills */}
            {recipientMode === 'category' && (
              <div style={sectionStyle}>
                <label style={labelStyle}>RENNKLASSEN AUSWÄHLEN</label>
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                  {CATEGORIES.map(cat => {
                    const active = selectedCats.includes(cat);
                    return (
                      <button
                        key={cat}
                        onClick={() => toggleCat(cat)}
                        style={{ ...mono, fontSize: 8, fontWeight: 700, letterSpacing: '0.08em', padding: '5px 11px', background: active ? 'rgba(var(--primary-rgb),0.12)' : 'transparent', border: `1px solid ${active ? 'rgba(var(--primary-rgb),0.5)' : 'rgba(59,73,75,0.3)'}`, color: active ? 'var(--primary)' : '#849495', cursor: 'pointer', transition: 'all 0.15s' }}
                      >
                        {active ? '✓ ' : ''}{cat}
                      </button>
                    );
                  })}
                </div>
                {selectedCats.length === 0 && (
                  <div style={{ ...mono, fontSize: 9, color: 'rgba(255,180,171,0.7)', marginTop: 8 }}>
                    Mindestens eine Rennklasse auswählen.
                  </div>
                )}
              </div>
            )}

            {/* Individual driver list */}
            {recipientMode === 'selected' && (
              <div style={sectionStyle}>
                <label style={labelStyle}>FAHRER AUSWÄHLEN ({selectedDriverIds.length} ausgewählt)</label>
                {drivers.length === 0 ? (
                  <div style={{ ...mono, fontSize: 10, color: 'var(--outline)' }}>Keine Fahrer gefunden.</div>
                ) : (
                  <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid rgba(59,73,75,0.3)', background: 'rgba(26,28,32,0.5)' }}>
                    {drivers.map(d => {
                      const daId = d.driver_account_id;
                      if (!daId) return null; // no account → can't receive push
                      const active = selectedDriverIds.includes(daId);
                      return (
                        <div
                          key={d.id}
                          onClick={() => toggleDriver(daId)}
                          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', cursor: 'pointer', background: active ? 'rgba(var(--primary-rgb),0.07)' : 'transparent', borderBottom: '1px solid rgba(59,73,75,0.15)', transition: 'background 0.1s' }}
                        >
                          <div style={{ width: 14, height: 14, border: `1px solid ${active ? 'var(--primary)' : 'rgba(59,73,75,0.5)'}`, background: active ? 'rgba(var(--primary-rgb),0.2)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            {active && <span style={{ color: 'var(--primary)', fontSize: 9, lineHeight: 1 }}>✓</span>}
                          </div>
                          <span style={{ ...mono, fontSize: 10, fontWeight: 700, color: active ? 'var(--on-surface)' : 'var(--on-surface-variant)' }}>
                            {d.name}
                          </span>
                          {d.category && (
                            <span style={{ ...mono, fontSize: 8, color: 'var(--outline)', marginLeft: 'auto' }}>{d.category}</span>
                          )}
                        </div>
                      );
                    }).filter(Boolean)}
                  </div>
                )}
                {selectedDriverIds.length === 0 && (
                  <div style={{ ...mono, fontSize: 9, color: 'rgba(255,180,171,0.7)', marginTop: 8 }}>
                    Mindestens einen Fahrer auswählen.
                  </div>
                )}
              </div>
            )}

            {/* Optional event link */}
            <div style={sectionStyle}>
              <label style={labelStyle}>EVENT-LINK (OPTIONAL)</label>
              <select
                value={selectedEventId}
                onChange={e => setSelectedEventId(e.target.value)}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value="">— Kein Event verknüpfen —</option>
                {events.map(ev => (
                  <option key={ev.id} value={ev.id}>
                    {ev.name}{ev.date ? ` (${new Date(ev.date).toLocaleDateString('de-DE')})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Send button + result */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <button
                onClick={handleSend}
                disabled={sending || !sendTitle.trim() || !sendBody.trim() || (recipientMode === 'category' && selectedCats.length === 0) || (recipientMode === 'selected' && selectedDriverIds.length === 0)}
                style={{ ...mono, fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', padding: '10px 24px', background: 'rgba(var(--primary-rgb),0.12)', border: '1px solid rgba(var(--primary-rgb),0.5)', color: 'var(--primary)', cursor: sending ? 'wait' : 'pointer', opacity: (sending || !sendTitle.trim() || !sendBody.trim()) ? 0.4 : 1, transition: 'all 0.15s' }}
                onMouseEnter={e => { if (!sending) e.currentTarget.style.background = 'rgba(var(--primary-rgb),0.2)'; }}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(var(--primary-rgb),0.12)'}
              >
                {sending ? 'SENDE…' : '◈ SENDEN'}
              </button>

              {sendResult && (
                sendResult.error ? (
                  <span style={{ ...mono, fontSize: 9, color: '#ffb4ab' }}>⚠ {sendResult.error}</span>
                ) : (
                  <span style={{ ...mono, fontSize: 9, color: 'var(--brand-secondary)' }}>
                    ✓ GESENDET — {sendResult.sent} zugestellt, {sendResult.failed} fehlgeschlagen
                  </span>
                )
              )}
            </div>
          </div>

          {/* History */}
          <div>
            <div style={{ ...mono, fontSize: 9, fontWeight: 700, letterSpacing: '0.18em', color: 'var(--outline)', marginBottom: 14 }}>
              VERLAUF
            </div>
            {historyLoading ? (
              <div style={{ ...mono, fontSize: 11, color: 'var(--outline)' }}>LADE…</div>
            ) : history.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', border: '1px solid rgba(59,73,75,0.2)', background: 'rgba(26,28,32,0.3)' }}>
                <div style={{ ...mono, fontSize: 10, color: 'var(--outline)', letterSpacing: '0.12em' }}>NOCH KEINE NOTIFICATIONS GESENDET</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {history.map(h => (
                  <div
                    key={h.id}
                    style={{ padding: '1rem 1.25rem', background: 'rgba(26,28,32,0.35)', border: '1px solid rgba(59,73,75,0.2)' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 6 }}>
                      <div>
                        <span style={{ ...mono, fontSize: 11, fontWeight: 700, color: 'var(--on-surface)' }}>
                          {h.title}
                        </span>
                        {h.event_name && (
                          <span style={{ ...mono, fontSize: 8, color: 'var(--primary)', marginLeft: 10, padding: '2px 7px', border: '1px solid rgba(var(--primary-rgb),0.3)', background: 'rgba(var(--primary-rgb),0.06)' }}>
                            ⬡ {h.event_name}
                          </span>
                        )}
                      </div>
                      <span style={{ ...mono, fontSize: 9, color: 'var(--outline)', flexShrink: 0 }}>
                        {timeAgo(h.sent_at)}
                      </span>
                    </div>
                    <div style={{ ...sans, fontSize: 13, color: 'var(--outline)', lineHeight: 1.5, marginBottom: 8 }}>
                      {h.body}
                    </div>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                      <span style={{ ...mono, fontSize: 8, color: '#849495', padding: '3px 8px', border: '1px solid rgba(59,73,75,0.3)' }}>
                        {RECIPIENT_MODE_LABELS[h.recipient_mode] || h.recipient_mode}
                        {h.recipient_mode === 'category' && h.recipient_meta?.categories?.length
                          ? ': ' + h.recipient_meta.categories.join(', ')
                          : ''}
                      </span>
                      <span style={{ ...mono, fontSize: 8, color: 'var(--brand-secondary)', padding: '3px 8px', border: '1px solid rgba(var(--brand-secondary-rgb),0.2)' }}>
                        ✓ {h.sent_count} ZUGESTELLT
                      </span>
                      {h.failed_count > 0 && (
                        <span style={{ ...mono, fontSize: 8, color: '#ffb4ab', padding: '3px 8px', border: '1px solid rgba(255,180,171,0.2)' }}>
                          ⚠ {h.failed_count} FEHLGESCHLAGEN
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Inbox tabs (team / fahrer) ── */}
      {activeTab !== 'senden' && (
        loading ? (
          <div style={{ ...mono, fontSize: 12, color: 'var(--outline)', letterSpacing: '0.1em' }}>LADE...</div>
        ) : notifications.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', border: '1px solid rgba(59,73,75,0.25)', background: 'rgba(26,28,32,0.4)' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>📭</div>
            <div style={{ ...mono, fontSize: 11, color: 'var(--outline)', letterSpacing: '0.12em' }}>KEINE NOTIFICATIONS</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {notifications.map(n => {
              const isEventReminder  = isDriverTab && n.type === 'event_reminder' && n.event_id;
              const isJoinRequest    = !isDriverTab && n.type === 'join_request'    && n.driver_account_id;
              const isInviteAccepted = !isDriverTab && n.type === 'invite_accepted' && n.driver_id;
              const existing    = isEventReminder ? regMap[n.event_id] : null;
              const isRegistered = existing?.status === 'participating' || existing?.status === 'assigned';
              const isDeclined   = existing?.status === 'not_participating';
              const isPendingReg = isEventReminder && regPending === n.event_id;

              return (
                <div
                  key={n.id}
                  style={{ padding: '1rem 1.25rem', background: n.read ? 'rgba(26,28,32,0.35)' : 'rgba(var(--primary-rgb),0.05)', border: `1px solid ${n.read ? 'rgba(59,73,75,0.2)' : 'rgba(var(--primary-rgb),0.2)'}`, transition: 'all 0.15s', position: 'relative' }}
                >
                  {!n.read && (
                    <div style={{ position: 'absolute', top: 18, left: 6, width: 5, height: 5, borderRadius: '50%', background: 'var(--primary)', boxShadow: '0 0 6px rgba(var(--primary-rgb),0.8)' }} />
                  )}

                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                    <div style={{ fontSize: 20, flexShrink: 0, opacity: n.read ? 0.5 : 1, marginLeft: 4 }}>
                      {TYPE_ICON[n.type] || '🔔'}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 3 }}>
                        <span style={{ ...mono, fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', color: n.read ? 'var(--on-surface-variant)' : 'var(--on-surface)' }}>
                          {n.title}
                        </span>
                        <span style={{ ...mono, fontSize: 9, color: 'var(--outline)', letterSpacing: '0.06em', flexShrink: 0 }}>
                          {timeAgo(n.created_at)}
                        </span>
                      </div>
                      <div style={{ ...sans, fontSize: 13, color: n.read ? 'var(--outline)' : 'var(--on-surface-variant)', lineHeight: 1.5 }}>
                        {n.body}
                      </div>
                    </div>
                    {!n.read && (
                      <button
                        onClick={() => markRead(n.id)}
                        title="Als gelesen markieren"
                        style={{ ...mono, fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', padding: '4px 10px', background: 'transparent', border: '1px solid rgba(var(--primary-rgb),0.25)', color: 'var(--primary)', cursor: 'pointer', flexShrink: 0, transition: 'all 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(var(--primary-rgb),0.1)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        ✓ GELESEN
                      </button>
                    )}
                  </div>

                  {(isEventReminder || isJoinRequest || isInviteAccepted) && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 12, marginLeft: 40 }}>
                      {isEventReminder && (
                        <>
                          <button
                            onClick={() => { navigate(`/events/${n.event_id}`); if (!n.read) markRead(n.id); }}
                            style={{ ...mono, fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', padding: '6px 14px', background: 'rgba(var(--brand-primary-rgb),0.08)', border: '1px solid rgba(var(--brand-primary-rgb),0.3)', color: 'var(--brand-primary)', cursor: 'pointer', transition: 'all 0.15s' }}
                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(var(--brand-primary-rgb),0.15)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'rgba(var(--brand-primary-rgb),0.08)'}
                          >
                            ◈ ZUM EVENT
                          </button>
                          {isRegistered ? (
                            <button
                              onClick={() => handleEventToggle(n)}
                              disabled={isPendingReg}
                              style={{ ...mono, fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', padding: '6px 14px', background: 'rgba(255,180,171,0.08)', border: '1px solid rgba(255,180,171,0.3)', color: '#ffb4ab', cursor: isPendingReg ? 'wait' : 'pointer', opacity: isPendingReg ? 0.5 : 1, transition: 'all 0.15s' }}
                              onMouseEnter={e => { if (!isPendingReg) e.currentTarget.style.background = 'rgba(255,180,171,0.15)'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,180,171,0.08)'; }}
                            >
                              {isPendingReg ? '…' : '✕ ABMELDEN'}
                            </button>
                          ) : (
                            <button
                              onClick={() => handleEventToggle(n)}
                              disabled={isPendingReg}
                              style={{ ...mono, fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', padding: '6px 14px', background: 'rgba(var(--brand-secondary-rgb),0.08)', border: '1px solid rgba(var(--brand-secondary-rgb),0.3)', color: 'var(--brand-secondary)', cursor: isPendingReg ? 'wait' : 'pointer', opacity: isPendingReg ? 0.5 : 1, transition: 'all 0.15s' }}
                              onMouseEnter={e => { if (!isPendingReg) e.currentTarget.style.background = 'rgba(var(--brand-secondary-rgb),0.15)'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(var(--brand-secondary-rgb),0.08)'; }}
                            >
                              {isPendingReg ? '…' : isDeclined ? '↩ ANMELDEN' : '+ ANMELDEN'}
                            </button>
                          )}
                          {existing && (
                            <span style={{ ...mono, fontSize: 8, fontWeight: 700, letterSpacing: '0.08em', padding: '6px 10px', alignSelf: 'center', color: isRegistered ? 'var(--brand-primary)' : '#ffb4ab', border: `1px solid ${isRegistered ? 'rgba(var(--brand-primary-rgb),0.2)' : 'rgba(255,180,171,0.2)'}`, background: isRegistered ? 'rgba(var(--brand-primary-rgb),0.06)' : 'rgba(255,180,171,0.06)' }}>
                              {isRegistered ? '✓ ANGEMELDET' : '⊘ ABGEMELDET'}
                            </span>
                          )}
                        </>
                      )}
                      {isJoinRequest && (
                        <button
                          onClick={() => { navigate('/drivers'); if (!n.read) markRead(n.id); }}
                          style={{ ...mono, fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', padding: '6px 14px', background: 'rgba(var(--brand-primary-rgb),0.08)', border: '1px solid rgba(var(--brand-primary-rgb),0.3)', color: 'var(--brand-primary)', cursor: 'pointer', transition: 'all 0.15s' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(var(--brand-primary-rgb),0.15)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'rgba(var(--brand-primary-rgb),0.08)'}
                        >
                          ◉ ZUR FAHRERLISTE
                        </button>
                      )}
                      {isInviteAccepted && (
                        <button
                          onClick={() => { navigate(`/drivers/${n.driver_id}`); if (!n.read) markRead(n.id); }}
                          style={{ ...mono, fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', padding: '6px 14px', background: 'rgba(var(--brand-primary-rgb),0.08)', border: '1px solid rgba(var(--brand-primary-rgb),0.3)', color: 'var(--brand-primary)', cursor: 'pointer', transition: 'all 0.15s' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(var(--brand-primary-rgb),0.15)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'rgba(var(--brand-primary-rgb),0.08)'}
                        >
                          ◉ ZUM FAHRERPROFIL
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}

export default Notifications;
